/**
 * postMessage bridge to the Moreta AiPrise browser extension.
 *
 * The extension's content script (running on this origin) relays two kinds of
 * traffic through `window.postMessage`:
 *
 *   extension → page   KYC actions decided on an AiPrise tab ("retry",
 *                      "decline", "existing account"). The page resolves the
 *                      AiPrise session id via the admin API and executes the
 *                      action with its OWN authenticated client — the access
 *                      token never crosses the boundary, and the server's
 *                      RBAC, audit log and validation apply unchanged. The
 *                      bridge is plain IPC, not an auth surface: any script
 *                      on this page could already call the API with the
 *                      signed-in admin's token, so it adds no capability a
 *                      compromised page wouldn't have. What it does enforce:
 *                      a fixed action allowlist, UUID-validated payloads and
 *                      same-window/same-origin message checks.
 *
 *   page → extension   "open these URLs in tabs" (the KYC queue's "Open all
 *                      in AiPrise" button — window.open would be popup-blocked
 *                      after the first). The extension only opens URLs on
 *                      provider/dashboard origins it recognises.
 *
 * Presence is a handshake: the content script announces `ready`, the page
 * announces `hello` on mount (covering both load orders). Without the
 * extension nothing here runs and the page behaves exactly as before.
 */

import type { QueryClient } from "@tanstack/react-query";
import { useSyncExternalStore } from "react";
import { toast } from "sonner";

import { api } from "@/api/client";
import { ApiError } from "@/api/errors";
import { KYC_KEY } from "@/api/queries/kyc";
import { REVIEW_SUMMARY_KEY } from "@/api/queries/queue-mutation";
import { usersKey } from "@/api/queries/users";
import type {
  AccountExistsResponse,
  DeclineKycResponse,
  KycSessionByAipriseResponse,
  RetryKycResponse,
} from "@/api/types";

/** `source` tags — who authored a message. Mirrored in the extension. */
const FROM_EXTENSION = "moreta-bridge-ext";
const FROM_PAGE = "moreta-bridge-page";

/**
 * The extension is distributed by this app: the Vite build zips the unpacked
 * source in `dashboard/extension/` and emits it under this root path (see
 * `extensionZipPlugin` in vite.config.ts, which mirrors the name).
 */
export const EXTENSION_DOWNLOAD_URL = "/moreta-aiprise-extension.zip";

/** The extension version this build ships (extension/manifest.json). */
export const SHIPPED_EXTENSION_VERSION = __EXTENSION_VERSION__;

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// ---------------------------------------------------------------------------
// Presence store (tiny external store so any component can useExtensionPresent)
// ---------------------------------------------------------------------------

let extensionPresent = false;
/** Version the content script reported in `ready`; null = never announced. */
let extensionVersion: string | null = null;
const presenceListeners = new Set<() => void>();

const setPresent = (value: boolean, version: string | null) => {
  if (extensionPresent === value && extensionVersion === version) return;
  extensionPresent = value;
  extensionVersion = version;
  presenceListeners.forEach((listener) => listener());
};

const subscribePresence = (listener: () => void) => {
  presenceListeners.add(listener);
  return () => {
    presenceListeners.delete(listener);
  };
};

/** true while the browser extension's content script has announced itself. */
export const useExtensionPresent = (): boolean =>
  useSyncExternalStore(subscribePresence, () => extensionPresent);

/**
 * Dotted-numeric version compare ("2.0" < "2.1" < "10.0"). Unparseable
 * segments compare equal so a malformed version never triggers a nag loop.
 */
const isOlderVersion = (installed: string, shipped: string): boolean => {
  const a = installed.split(".");
  const b = shipped.split(".");
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const left = Number(a[i] ?? 0);
    const right = Number(b[i] ?? 0);
    if (Number.isNaN(left) || Number.isNaN(right)) return false;
    if (left !== right) return left < right;
  }
  return false;
};

/**
 * true while the extension is present but older than the version this build
 * distributes. A `ready` without a version (installs predating the version
 * handshake) counts as outdated.
 */
export const useExtensionUpdateAvailable = (): boolean =>
  useSyncExternalStore(
    subscribePresence,
    () =>
      extensionPresent &&
      (extensionVersion === null ||
        isOlderVersion(extensionVersion, SHIPPED_EXTENSION_VERSION)),
  );

// ---------------------------------------------------------------------------
// Page → extension requests
// ---------------------------------------------------------------------------

let requestSeq = 0;
const pendingPageRequests = new Map<
  string,
  { resolve: (ok: boolean) => void; timer: ReturnType<typeof setTimeout> }
>();

/** How many AiPrise tabs the extension keeps open at once during "Open all". */
export const OPEN_ALL_WINDOW = 10;
const OPEN_TABS_TIMEOUT_MS = 15_000;

/**
 * Ask the extension to open URLs as a rolling batch: the first
 * OPEN_ALL_WINDOW open immediately, and each time one of those tabs closes
 * (actions auto-close their tab) the extension opens the next, until the
 * batch is done. Resolves false when the extension is absent or doesn't
 * answer — callers should fall back or toast.
 */
export const openTabsViaExtension = (urls: string[]): Promise<boolean> => {
  if (!extensionPresent || urls.length === 0) return Promise.resolve(false);
  const id = `page-${Date.now()}-${requestSeq++}`;
  return new Promise<boolean>((resolve) => {
    // The extension only replies once the background has created the first
    // OPEN_ALL_WINDOW tabs sequentially (after a possible service-worker
    // wake), so this has to outlast a slow batch, not just a lost message.
    const timer = setTimeout(() => {
      pendingPageRequests.delete(id);
      resolve(false);
    }, OPEN_TABS_TIMEOUT_MS);
    pendingPageRequests.set(id, { resolve, timer });
    window.postMessage(
      {
        source: FROM_PAGE,
        type: "request",
        id,
        action: "openTabs",
        payload: { urls, maxConcurrent: OPEN_ALL_WINDOW },
      },
      window.location.origin,
    );
  });
};

// ---------------------------------------------------------------------------
// Extension → page actions
// ---------------------------------------------------------------------------

const isBridgeMessage = (
  data: unknown,
): data is { source: string; type: string } & Record<string, unknown> =>
  typeof data === "object" &&
  data !== null &&
  typeof (data as Record<string, unknown>).source === "string" &&
  typeof (data as Record<string, unknown>).type === "string";

const errorMessage = (error: unknown): string =>
  error instanceof ApiError
    ? error.message
    : error instanceof Error
      ? error.message
      : "Unknown error";

const resolveByAipriseId = async (aipriseSessionId: string) => {
  const { session } = await api.get<KycSessionByAipriseResponse>(
    `/admin/kyc/sessions/by-aiprise/${encodeURIComponent(aipriseSessionId)}`,
  );
  return session;
};

/**
 * Execute one allowlisted action. Throws on failure; the caller wraps the
 * result into the response envelope.
 */
const runAction = async (
  queryClient: QueryClient,
  action: string,
  payload: Record<string, unknown>,
): Promise<Record<string, unknown>> => {
  if (action === "ping") return { present: true };

  // Fire-and-forget nudge after ANY successful action on an AiPrise tab —
  // including AiPrise-native Approve/Reject, which never pass through this
  // bridge and reach the backend via webhook. Invalidate now (bridge-routed
  // actions are already committed) and again shortly after, giving that
  // webhook time to land so the session actually leaves "Needs review".
  if (action === "kyc.refresh") {
    const invalidate = () => {
      void queryClient.invalidateQueries({ queryKey: KYC_KEY });
      void queryClient.invalidateQueries({ queryKey: REVIEW_SUMMARY_KEY });
    };
    invalidate();
    setTimeout(invalidate, 10_000);
    return {};
  }

  const aipriseSessionId = payload.aipriseSessionId;
  if (typeof aipriseSessionId !== "string" || !UUID_RE.test(aipriseSessionId)) {
    throw new Error("aipriseSessionId must be a UUID");
  }
  const session = await resolveByAipriseId(aipriseSessionId);

  const finish = (summary: string) => {
    toast.info(`Extension: ${summary}`);
    void queryClient.invalidateQueries({ queryKey: KYC_KEY });
    void queryClient.invalidateQueries({ queryKey: REVIEW_SUMMARY_KEY });
    void queryClient.invalidateQueries({ queryKey: usersKey(session.userId) });
  };

  switch (action) {
    case "kyc.retry": {
      const result = await api.post<RetryKycResponse>("/admin/retry-kyc", {
        sessionId: session.id,
      });
      finish(`retry sent to ${session.user.email}`);
      return { ...result };
    }
    case "kyc.decline": {
      const result = await api.post<DeclineKycResponse>("/admin/decline-kyc", {
        sessionId: session.id,
      });
      finish(`declined session of ${session.user.email}`);
      return { ...result };
    }
    case "kyc.accountExists": {
      const notified = await api.post<AccountExistsResponse>(
        "/admin/account-exists",
        { userId: session.userId },
      );
      // Mirror the KYC page's default: decline the duplicate session in the
      // same breath — but only a row that can actually be declined, so the
      // customer isn't notified and then handed a 400.
      let declined = false;
      if (payload.alsoDecline !== false && session.status === "needs_review") {
        await api.post<DeclineKycResponse>("/admin/decline-kyc", {
          sessionId: session.id,
        });
        declined = true;
      }
      finish(
        `${session.user.email} notified about their existing account${declined ? " and declined" : ""}`,
      );
      return { ...notified, sessionId: session.id, declined };
    }
    default:
      throw new Error(`Unknown bridge action: ${action}`);
  }
};

// ---------------------------------------------------------------------------
// Listener — started once by <ExtensionBridge/> inside AuthProvider (so the
// API client is signed in). Returns the cleanup function.
// ---------------------------------------------------------------------------

export function startExtensionBridge(queryClient: QueryClient): () => void {
  const onMessage = (event: MessageEvent) => {
    // Only same-window, same-origin traffic — the content script posts on
    // this exact window. Anything else (iframes, other origins) is ignored.
    if (event.source !== window) return;
    if (event.origin !== window.location.origin) return;
    const data: unknown = event.data;
    if (!isBridgeMessage(data)) return;

    if (data.source === FROM_EXTENSION && data.type === "ready") {
      setPresent(true, typeof data.version === "string" ? data.version : null);
      return;
    }

    if (data.source === FROM_EXTENSION && data.type === "response") {
      const id = typeof data.id === "string" ? data.id : "";
      const pending = pendingPageRequests.get(id);
      if (pending) {
        pendingPageRequests.delete(id);
        clearTimeout(pending.timer);
        pending.resolve(data.ok === true);
      }
      return;
    }

    if (data.source === FROM_EXTENSION && data.type === "request") {
      const { id, action, payload: rawPayload } = data;
      if (typeof id !== "string" || typeof action !== "string") return;
      const payload =
        typeof rawPayload === "object" && rawPayload !== null
          ? (rawPayload as Record<string, unknown>)
          : {};
      void runAction(queryClient, action, payload)
        .then((result) => {
          window.postMessage(
            {
              source: FROM_PAGE,
              type: "response",
              id,
              ok: true,
              result,
            },
            window.location.origin,
          );
        })
        .catch((error: unknown) => {
          window.postMessage(
            {
              source: FROM_PAGE,
              type: "response",
              id,
              ok: false,
              error: errorMessage(error),
            },
            window.location.origin,
          );
        });
    }
  };

  window.addEventListener("message", onMessage);
  // Announce ourselves — the content script may have loaded first and
  // already posted `ready` before this listener existed.
  window.postMessage(
    { source: FROM_PAGE, type: "hello" },
    window.location.origin,
  );
  return () => window.removeEventListener("message", onMessage);
}
